import { PlDialog } from "./pl--dialog.js"
import { PentEvents, normalizeSectionId } from "./pent-core/index.js"
import { sectionRenderer } from "./pent-core/index.js"

/**
 * PlCartDrawer - Custom element for cart drawer
 * Extends PlDialog and listens for cart:updated events from pent-core
 */
class PlCartDrawer extends PlDialog {
  #abortController = null

  connectedCallback() {
    super.connectedCallback()

    // Parent is done, so we can proceed immediately
    this.#attachCartListeners()
  }

  #attachCartListeners() {
    // Listen for cart:updated events from pent-core
    document.addEventListener(PentEvents.CART_UPDATED, this.#handleCartUpdated.bind(this))

    // Listen for manual open requests
    document.addEventListener("cart:open", () => this.open())

    // Auto-bind all [data-cart-open] buttons
    document.querySelectorAll("[data-cart-open]").forEach((button) => {
      button.addEventListener("click", () => this.open())
    })

    // Handle close button clicks inside drawer
    const closeButtons = this.querySelectorAll("[data-cart-drawer-close]")
    closeButtons.forEach((btn) => {
      btn.addEventListener("click", () => this.close())
    })

    // Handle remove item clicks (use event delegation for dynamic content)
    this.addEventListener("click", (event) => {
      const removeBtn = event.target.closest("[data-cart-remove]")
      if (removeBtn) {
        const lineKey = removeBtn.dataset.cartRemove
        if (lineKey) {
          this.#removeItem(lineKey, removeBtn)
        }
      }
    })
  }

  /**
   * Removes an item from the cart
   * @param {string} lineKey - The cart line item key
   * @param {HTMLElement} [triggerElement] - The element that triggered the removal
   */
  async #removeItem(lineKey, triggerElement) {
    // Optimistic UI: Hide the line item immediately
    if (triggerElement) {
      const lineItem = triggerElement.closest(".pl--cart-drawer-line-item")
      if (lineItem) {
        lineItem.style.display = "none"
      }
    }

    try {
      const url = (window.Shopify?.routes?.root || "/") + "cart/change.js"

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      })

      if (!response.ok) {
        console.error("Failed to remove item:", response.status)

        // Revert optimistic update on error
        if (triggerElement) {
          const lineItem = triggerElement.closest(".pl--cart-drawer-line-item")
          if (lineItem) {
            lineItem.style.display = ""
          }
        }
        return
      }

      const cart = await response.json()

      // Import and use dispatchCartUpdated
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
          bubbles: true,
        })
      )
    } catch (error) {
      console.error("Failed to remove item:", error)
    }
  }

  async #handleCartUpdated(event) {
    const { cart, addedItem } = event.detail

    // Only auto-open drawer when a NEW item is added (not quantity changes)
    if (addedItem) {
      this.open()
    }

    // Find section ID more reliably
    const sectionElement = this.closest(".shopify-section")
    const sectionId = sectionElement?.id

    if (sectionId) {
      // Normalize the section ID (remove shopify-section- prefix if present)
      const normalizedId = normalizeSectionId(sectionId)
      // Always re-render to update counts, items, etc.
      await this.#updateCartContent(normalizedId)
    }

    // Update cart count
    this.#updateCartCount(cart?.item_count || 0)
  }

  async #updateCartContent(sectionId) {
    if (this.#abortController) {
      this.#abortController.abort()
    }

    const wasOpen = this.isOpen
    console.log("[cart-drawer] Before morph - wasOpen:", wasOpen)

    this.#abortController = new AbortController()

    try {
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
      })
      console.log("[cart-drawer] After morph - still connected:", this.isConnected)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Failed to update cart drawer:", error)
      }
    }

    this.#abortController = undefined

    if (wasOpen) {
      console.log("[cart-drawer] Re-opening drawer...")
      this.reinitDialog()
      console.log("[cart-drawer] After reinit - dialog found:", !!this.querySelector("dialog"))
      this.open()
      console.log("[cart-drawer] After open() - isOpen:", this.isOpen)

      // Check again after a frame
      requestAnimationFrame(() => {
        console.log("[cart-drawer] After rAF - isOpen:", this.isOpen)
        requestAnimationFrame(() => {
          console.log("[cart-drawer] After 2nd rAF - isOpen:", this.isOpen)
        })
      })
    }
  }

  #updateCartCount(count) {
    // Dispatch event for cart badge updates
    document.dispatchEvent(
      new CustomEvent("cart:count-updated", {
        detail: { count },
        bubbles: true,
      })
    )
  }
}

if (!customElements.get("pl-cart-drawer")) {
  customElements.define("pl-cart-drawer", PlCartDrawer)
}

export { PlCartDrawer }
